import { Button, Grid2, Stack, Typography, useTheme } from "@mui/material";
import { Link, Outlet } from "react-router";
import { TabHeaderCatalog } from "../TabHeaderCatalog";
import { useCatalogContext } from "../Summary/context/useCatalogContext";
import { CatalogSideInfo } from "./CatelogSideInfo";
import { useTranslation } from "react-i18next";
import { FoodsIcon, FoodsIllustrations } from "@foodpilot/foods";
import { AddActionButton } from "./AddActionButton";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { ActionOverviewSkeleton } from "src/components/Actions/Common/ActionOverviewCard";

export const CatalogItemLayout = () => {
    const theme = useTheme();

    const { urls } = useActionsContext();
    const { areActionsReady, currentAction } = useCatalogContext();

    if (!areActionsReady) {
        return (
            <Stack width="100%" padding={theme.spacing(0, 6)}>
                <Stack flexDirection="row" justifyContent="space-between" padding={theme.spacing(2, 0)}>
                    <TabHeaderCatalog />
                </Stack>
                <Grid2 container spacing={theme.spacing(2)} columns={12}>
                    <Grid2 size={{ xs: 12, md: 9 }}>
                        <ActionOverviewSkeleton variant="catalog" />
                    </Grid2>
                </Grid2>
            </Stack>
        );
    }

    if (currentAction === undefined) {
        return null;
    }

    const url =
        currentAction.actionPlanActionId !== undefined ?
            urls.editPlanning(currentAction.actionPlanActionId)
        :   undefined;

    const hasDescription = Boolean(currentAction.description?.trim());
    const hasImage = Boolean(currentAction.imageUrl);
    const hasNoOverview = !hasDescription && !hasImage;
    const overviewSize = hasDescription ? 9 : "auto";
    const sideInfoSize = hasDescription ? 3 : "grow";

    return (
        <Stack width={"100%"} padding={theme.spacing(0, 6)}>
            <Stack flexDirection={"row"} justifyContent={"space-between"} padding={theme.spacing(2, 0)}>
                <TabHeaderCatalog />
            </Stack>
            {hasNoOverview ?
                <ActionNotTakenWithNoDescription urlToAction={url} />
            :   <Grid2
                    container
                    spacing={theme.spacing(hasImage ? 3 : 2)}
                    columns={hasImage ? 12 : { xl: 12, lg: 12, md: 8, sm: 4 }}
                >
                    <Grid2 size={hasImage ? { xs: 12, md: overviewSize } : 9}>
                        <Outlet />
                    </Grid2>
                    <Grid2 size={hasImage ? { xs: 12, md: sideInfoSize } : 3}>
                        <CatalogSideInfo orientation={hasDescription ? "vertical" : "horizontal"} />
                    </Grid2>
                </Grid2>
            }
        </Stack>
    );
};

type Props = {
    urlToAction: string | undefined;
};
const ActionNotTakenWithNoDescription = (props: Props) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const { urlToAction } = props;

    return (
        <Stack
            padding={theme.spacing(3)}
            boxShadow={`0px 2px 8px 0px #2A2B270A`}
            border={`1px solid ${theme.custom.grey[600]}`}
            borderRadius={`12px`}
            bgcolor={"white"}
        >
            <Grid2 container alignItems={"center"} spacing={theme.spacing(3)}>
                <Grid2 size={{ md: 2, sm: 3 }}>
                    <FoodsIllustrations illustration={"selectAction"} />
                </Grid2>
                <Grid2 size={{ md: 8, sm: 6 }}>
                    <Typography variant="body">
                        {urlToAction !== undefined ?
                            t("action_plan.edit_action_info", "This action is already added")
                        :   t(
                                "action_plan.add_action_info",
                                "Add this action to your action plan in order to simulate its impact and get closer to your goal.",
                            )
                        }
                    </Typography>
                </Grid2>
                <Grid2 size={{ md: 2, sm: 3 }} textAlign={"end"}>
                    {urlToAction !== undefined ?
                        <Link to={urlToAction}>
                            <Button startIcon={<FoodsIcon size={2} icon="edit" />} variant="primary">
                                {t("action_plan.edit_my_action", "Edit my action")}
                            </Button>
                        </Link>
                    :   <AddActionButton />}
                </Grid2>
            </Grid2>
        </Stack>
    );
};
